import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Plus,
  Trash,
  PencilSimple,
  Truck,
  Wrench,
  MagnifyingGlass,
  CaretDown,
  CaretLeft,
  CaretRight,
  Warning,
  UploadSimple,
  Clock,
} from "@phosphor-icons/react";
import { toISO, fromISO, MONTH_SHORT } from "@/lib/dates";

const SERVICE_TYPES = ["A", "AB", "C", "M"];
const TYPE_META = {
  A: { label: "A", desc: "A service", bg: "bg-sky-500", text: "text-white" },
  AB: { label: "AB", desc: "AB service", bg: "bg-indigo-600", text: "text-white" },
  C: { label: "C", desc: "C service", bg: "bg-amber-500", text: "text-amber-950" },
  M: { label: "M", desc: "M / repair", bg: "bg-rose-600", text: "text-white" },
};

function todayISO() {
  return toISO(new Date());
}

export default function FleetPage() {
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState(null);
  const [heatmapMonth, setHeatmapMonth] = useState(new Date(2026, 3, 1));

  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({ fleet_number: "", route_id: null, notes: "" });

  const [servicesFor, setServicesFor] = useState(null); // vehicle object or null
  const [serviceForm, setServiceForm] = useState({
    service_date: "2026-04-01",
    service_type: "A",
    notes: "",
  });
  const [editingService, setEditingService] = useState(null);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [v, r, s] = await Promise.all([
        api.listVehicles(),
        api.listRoutes(),
        api.listVehicleServices(),
      ]);
      setVehicles(v);
      setRoutes(r);
      setServices(s);
    } catch {
      toast.error("Failed to load fleet");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const routeById = useMemo(() => {
    const m = {};
    for (const r of routes) m[r.id] = r;
    return m;
  }, [routes]);

  const servicesByVehicle = useMemo(() => {
    const m = {};
    for (const s of services) {
      if (!m[s.vehicle_id]) m[s.vehicle_id] = [];
      m[s.vehicle_id].push(s);
    }
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.service_date.localeCompare(b.service_date));
    return m;
  }, [services]);

  const today = todayISO();
  const inServiceToday = useMemo(
    () => new Set(services.filter((s) => s.service_date === today).map((s) => s.vehicle_id)),
    [services, today]
  );
  const spareCount = vehicles.filter((v) => !v.route_id).length;

  const filteredVehicles = useMemo(() => {
    let list = vehicles;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (v) =>
          v.fleet_number.toLowerCase().includes(q) ||
          (routeById[v.route_id]?.code || "").toLowerCase().includes(q)
      );
    }
    if (dateFilter) {
      const ids = new Set(services.filter((s) => s.service_date === dateFilter).map((s) => s.vehicle_id));
      list = list.filter((v) => ids.has(v.id));
    }
    if (typeFilter !== "ALL") {
      const ids = new Set(services.filter((s) => s.service_type === typeFilter).map((s) => s.vehicle_id));
      list = list.filter((v) => ids.has(v.id));
    }
    return list;
  }, [vehicles, search, dateFilter, typeFilter, services, routeById]);

  const typeCounts = useMemo(() => {
    const c = { A: 0, AB: 0, C: 0, M: 0 };
    for (const s of services) c[s.service_type] = (c[s.service_type] || 0) + 1;
    return c;
  }, [services]);

  // ---- Vehicle CRUD ----
  const openNewVehicle = () => {
    setEditingVehicle(null);
    setVehicleForm({ fleet_number: "", route_id: null, notes: "" });
    setVehicleDialogOpen(true);
  };
  const openEditVehicle = (v) => {
    setEditingVehicle(v);
    setVehicleForm({ fleet_number: v.fleet_number, route_id: v.route_id, notes: v.notes || "" });
    setVehicleDialogOpen(true);
  };
  const saveVehicle = async () => {
    if (!vehicleForm.fleet_number.trim()) {
      toast.error("Fleet number required");
      return;
    }
    try {
      if (editingVehicle) await api.updateVehicle(editingVehicle.id, vehicleForm);
      else await api.createVehicle(vehicleForm);
      setVehicleDialogOpen(false);
      await load();
      toast.success(editingVehicle ? "Vehicle updated" : "Vehicle added");
    } catch {
      toast.error("Save failed");
    }
  };
  const delVehicle = async (v) => {
    if (!window.confirm(`Delete ${v.fleet_number}? This also removes all its service days.`)) return;
    try {
      await api.deleteVehicle(v.id);
      await load();
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  // ---- Service CRUD ----
  const openServices = (v) => {
    setServicesFor(v);
    setEditingService(null);
    setServiceForm({ service_date: "2026-04-01", service_type: "A", notes: "" });
  };
  const startEditService = (s) => {
    setEditingService(s);
    setServiceForm({
      service_date: s.service_date,
      service_type: s.service_type,
      notes: s.notes || "",
    });
  };
  const saveService = async () => {
    if (!servicesFor) return;
    if (!serviceForm.service_date) {
      toast.error("Date required");
      return;
    }
    try {
      const payload = { ...serviceForm, vehicle_id: servicesFor.id };
      const result = editingService
        ? await api.updateVehicleService(editingService.id, payload)
        : await api.createVehicleService(payload);
      setEditingService(null);
      setServiceForm({ service_date: serviceForm.service_date, service_type: "A", notes: "" });
      await load();
      if (result?.auto_override) {
        toast.success(
          `Service added — auto-swapped to ${result.auto_override.fleet_number}${
            result.auto_override.is_spare ? " (spare)" : ""
          }`
        );
      } else {
        toast.success(editingService ? "Service updated" : "Service added");
      }
    } catch {
      toast.error("Save failed");
    }
  };
  const delService = async (s) => {
    if (!window.confirm(`Remove ${s.service_type} service on ${s.service_date}?`)) return;
    try {
      await api.deleteVehicleService(s.id);
      await load();
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  // ---- Bulk import ----
  const parseImportLines = (text) => {
    const rows = [];
    for (const raw of text.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line) continue;
      // Split on tab, comma or 2+ spaces
      const parts = line.split(/[\t,]|\s{2,}/).map((p) => p.trim()).filter(Boolean);
      if (parts.length < 2) continue;
      rows.push({ route_code: parts[0], fleet_number: parts.slice(1).join(" ") });
    }
    return rows;
  };
  const importRows = useMemo(() => parseImportLines(importText), [importText]);
  const doImport = async () => {
    if (importRows.length === 0) {
      toast.error("Nothing to import — paste rows first");
      return;
    }
    setImporting(true);
    try {
      const res = await api.bulkVehicles(importRows);
      toast.success(
        `Updated ${res.updated} · Created ${res.created}${
          res.unmatched.length ? ` · Skipped ${res.unmatched.length}` : ""
        }`
      );
      setImportDialogOpen(false);
      setImportText("");
      await load();
    } catch {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  // Service-reminder helpers: days until next upcoming service per vehicle
  const daysUntil = (iso) => {
    const t = fromISO(iso).getTime();
    const now = fromISO(today).getTime();
    return Math.round((t - now) / 86400000);
  };
  const nextServiceForVehicle = (vehicleId) => {
    const arr = (servicesByVehicle[vehicleId] || []).filter((s) => s.service_date >= today);
    return arr[0] || null;
  };

  return (
    <div className="p-6 min-h-screen">
      <header className="flex items-end justify-between border-b-2 border-black pb-4 mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight leading-none">FLEET ROTA</h1>
          <div className="text-[10px] font-mono-plex tracking-widest text-zinc-500 mt-2">
            SERVICE TYPES A · AB · C · M — {vehicles.length} VEHICLES · {services.length} SERVICE DAYS
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setImportDialogOpen(true)}
            data-testid="import-fleet-btn"
            className="rounded-none bg-white text-black border border-black hover:bg-zinc-100 h-10 font-mono-plex text-xs tracking-widest active:scale-[0.98]"
          >
            <UploadSimple size={14} weight="bold" className="mr-2" /> IMPORT FLEET
          </Button>
          <Button
            onClick={openNewVehicle}
            data-testid="add-vehicle-btn"
            className="rounded-none bg-black text-white hover:bg-zinc-800 h-10 font-mono-plex text-xs tracking-widest active:scale-[0.98]"
          >
            <Plus size={14} weight="bold" className="mr-2" /> ADD VEHICLE
          </Button>
        </div>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Kpi label="TOTAL VEHICLES" value={vehicles.length} icon={<Truck size={20} weight="bold" />} testid="kpi-total" />
        <Kpi
          label="IN SERVICE TODAY"
          value={inServiceToday.size}
          icon={<Wrench size={20} weight="bold" />}
          accent="rose"
          testid="kpi-inservice"
        />
        <Kpi label="SPARE VEHICLES" value={spareCount} testid="kpi-spare" />
        <div
          className="border-2 border-black bg-white p-4 flex items-center justify-between"
          data-testid="kpi-type-breakdown"
        >
          <div className="flex-1">
            <div className="text-[10px] font-mono-plex tracking-widest text-zinc-500">SERVICES BY TYPE</div>
            <div className="mt-2 flex items-center gap-2">
              {SERVICE_TYPES.map((t) => (
                <div key={t} className="flex items-center gap-1">
                  <span
                    className={`inline-block px-1.5 py-0.5 text-[10px] font-mono-plex font-bold ${TYPE_META[t].bg} ${TYPE_META[t].text}`}
                  >
                    {t}
                  </span>
                  <span className="font-heading font-bold text-sm">{typeCounts[t]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Heatmap */}
      <div className="mb-6">
        <ServiceHeatmap
          monthDate={heatmapMonth}
          onMonthChange={setHeatmapMonth}
          services={services}
          selectedDate={dateFilter}
          onSelectDate={setDateFilter}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search fleet number / route…"
            data-testid="fleet-search"
            className="pl-8 h-9 w-64 border-black rounded-none font-mono-plex text-xs"
          />
        </div>
        <div className="flex border border-black" data-testid="fleet-type-filter">
          {["ALL", ...SERVICE_TYPES].map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              data-testid={`fleet-type-${t}`}
              className={`px-3 py-2 text-[10px] font-mono-plex tracking-widest font-semibold transition-colors ${
                typeFilter === t ? "bg-black text-white" : "bg-white hover:bg-zinc-100"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        {(dateFilter || typeFilter !== "ALL" || search) && (
          <button
            onClick={() => {
              setDateFilter(null);
              setTypeFilter("ALL");
              setSearch("");
            }}
            className="text-[10px] font-mono-plex tracking-widest underline hover:no-underline"
          >
            CLEAR FILTERS
          </button>
        )}
      </div>

      {/* Vehicles table */}
      <div className="border-2 border-black bg-white" data-testid="fleet-table">
        <div className="grid grid-cols-[140px_120px_1fr_100px_140px] font-heading font-bold text-[10px] tracking-widest border-b-2 border-black bg-zinc-50">
          <div className="px-4 py-3">FLEET #</div>
          <div className="px-4 py-3 border-l border-zinc-300">ROUTE</div>
          <div className="px-4 py-3 border-l border-zinc-300">SERVICES (UPCOMING)</div>
          <div className="px-4 py-3 border-l border-zinc-300 text-right">STATUS</div>
          <div className="px-4 py-3 border-l border-zinc-300 text-right">ACTIONS</div>
        </div>
        {loading && (
          <div className="p-6 font-mono-plex text-xs text-zinc-500">LOADING…</div>
        )}
        {!loading && filteredVehicles.length === 0 && (
          <div className="p-8 text-center font-mono-plex text-xs text-zinc-500">
            NO VEHICLES MATCH FILTERS
          </div>
        )}
        {filteredVehicles.map((v) => {
          const route = routeById[v.route_id];
          const vServices = servicesByVehicle[v.id] || [];
          const upcoming = vServices.filter((s) => s.service_date >= today).slice(0, 5);
          const isInServiceToday = inServiceToday.has(v.id);
          return (
            <div
              key={v.id}
              className="grid grid-cols-[140px_120px_1fr_100px_140px] border-b border-zinc-200 hover:bg-zinc-50"
              data-testid={`vehicle-row-${v.id}`}
            >
              <div className="px-4 py-3 flex items-center gap-2 font-mono-plex text-sm font-semibold">
                <Truck size={14} className="text-zinc-500" />
                {v.fleet_number}
              </div>
              <div className="px-4 py-3 border-l border-zinc-200 font-mono-plex text-xs">
                {route ? (
                  <span className="bg-black text-white px-2 py-0.5 text-[10px] tracking-widest">
                    {route.code}
                  </span>
                ) : (
                  <span className="border border-zinc-400 text-zinc-600 px-2 py-0.5 text-[10px] tracking-widest">
                    SPARE
                  </span>
                )}
              </div>
              <div className="px-4 py-3 border-l border-zinc-200 flex items-center gap-1 flex-wrap">
                {upcoming.length === 0 ? (
                  <span className="font-mono-plex text-[10px] text-zinc-400 tracking-widest">
                    NONE SCHEDULED
                  </span>
                ) : (
                  upcoming.map((s) => (
                    <span
                      key={s.id}
                      className="inline-flex items-center gap-1 font-mono-plex text-[10px] border border-zinc-300 pl-1"
                    >
                      <span
                        className={`px-1 py-0.5 font-bold ${TYPE_META[s.service_type].bg} ${TYPE_META[s.service_type].text}`}
                      >
                        {s.service_type}
                      </span>
                      <span className="pr-1">{s.service_date.slice(5)}</span>
                    </span>
                  ))
                )}
                {vServices.length > upcoming.length && (
                  <span className="font-mono-plex text-[10px] text-zinc-500 tracking-widest">
                    +{vServices.length - upcoming.length} MORE
                  </span>
                )}
              </div>
              <div className="px-4 py-3 border-l border-zinc-200 text-right">
                {(() => {
                  if (isInServiceToday) {
                    return (
                      <span className="inline-flex items-center gap-1 bg-rose-600 text-white text-[9px] font-mono-plex tracking-widest px-2 py-0.5">
                        <Warning size={10} weight="bold" />
                        IN SVC
                      </span>
                    );
                  }
                  const next = nextServiceForVehicle(v.id);
                  if (next) {
                    const d = daysUntil(next.service_date);
                    if (d >= 0 && d <= 14) {
                      const bg = d <= 3 ? "bg-rose-500 text-white" : d <= 7 ? "bg-amber-400 text-amber-950" : "bg-zinc-300 text-zinc-900";
                      return (
                        <span
                          className={`inline-flex items-center gap-1 text-[9px] font-mono-plex tracking-widest px-2 py-0.5 ${bg}`}
                          data-testid={`svc-reminder-${v.id}`}
                          title={`${next.service_type} service on ${next.service_date}`}
                        >
                          <Clock size={10} weight="bold" />
                          {d === 0 ? "TODAY" : `IN ${d}D`}
                        </span>
                      );
                    }
                  }
                  return (
                    <span className="text-[9px] font-mono-plex tracking-widest text-emerald-700">
                      AVAILABLE
                    </span>
                  );
                })()}
              </div>
              <div className="px-4 py-3 border-l border-zinc-200 flex items-center justify-end gap-2">
                <button
                  onClick={() => openServices(v)}
                  data-testid={`services-btn-${v.id}`}
                  className="px-2 py-1 border border-black hover:bg-black hover:text-white transition-colors font-mono-plex text-[10px] tracking-widest"
                >
                  SERVICES
                </button>
                <button
                  onClick={() => openEditVehicle(v)}
                  data-testid={`edit-vehicle-${v.id}`}
                  className="p-1.5 border border-black hover:bg-black hover:text-white transition-colors"
                >
                  <PencilSimple size={12} weight="bold" />
                </button>
                <button
                  onClick={() => delVehicle(v)}
                  data-testid={`delete-vehicle-${v.id}`}
                  className="p-1.5 border border-black hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                >
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Vehicle dialog */}
      <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#0A0A0A] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight">
              {editingVehicle ? "EDIT VEHICLE" : "ADD VEHICLE"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">
                FLEET NUMBER
              </label>
              <Input
                value={vehicleForm.fleet_number}
                onChange={(e) =>
                  setVehicleForm({ ...vehicleForm, fleet_number: e.target.value.toUpperCase() })
                }
                data-testid="vehicle-form-number"
                className="rounded-none border-black font-mono-plex uppercase"
                placeholder="e.g. AB12 CDE"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">
                PRIMARY ROUTE
              </label>
              <RoutePicker
                routes={routes}
                value={vehicleForm.route_id}
                onChange={(id) => setVehicleForm({ ...vehicleForm, route_id: id })}
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono-plex tracking-widest mb-1.5">
                NOTES <span className="text-zinc-400 normal-case tracking-normal">(optional)</span>
              </label>
              <Input
                value={vehicleForm.notes}
                onChange={(e) => setVehicleForm({ ...vehicleForm, notes: e.target.value })}
                data-testid="vehicle-form-notes"
                className="rounded-none border-black font-mono-plex text-xs"
                placeholder="e.g. RCV, capacity, depot"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setVehicleDialogOpen(false)}
              className="px-4 py-2 border border-black text-xs font-mono-plex tracking-widest hover:bg-zinc-100"
            >
              CANCEL
            </button>
            <button
              onClick={saveVehicle}
              data-testid="vehicle-form-save"
              className="px-4 py-2 bg-black text-white text-xs font-mono-plex tracking-widest hover:bg-zinc-800 active:scale-[0.98]"
            >
              SAVE
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Services drawer */}
      <Dialog open={!!servicesFor} onOpenChange={(o) => !o && setServicesFor(null)}>        <DialogContent
          className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#0A0A0A] max-w-lg"
          data-testid="services-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight">
              SERVICE DAYS · {servicesFor?.fleet_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border border-zinc-300 p-3 bg-zinc-50">
              <div className="text-[10px] font-mono-plex tracking-widest text-zinc-500 mb-2">
                {editingService ? "EDIT SERVICE DAY" : "ADD SERVICE DAY"}
              </div>
              <div className="grid grid-cols-[130px_1fr] gap-3">
                <div>
                  <label className="block text-[9px] font-mono-plex tracking-widest mb-1">DATE</label>
                  <Input
                    type="date"
                    value={serviceForm.service_date}
                    onChange={(e) =>
                      setServiceForm({ ...serviceForm, service_date: e.target.value })
                    }
                    data-testid="service-form-date"
                    className="rounded-none border-black font-mono-plex text-xs h-8"
                  />
                </div>
                <div>
                  <label className="block text-[9px] font-mono-plex tracking-widest mb-1">TYPE</label>
                  <div className="flex border border-black h-8">
                    {SERVICE_TYPES.map((t) => (
                      <button
                        key={t}
                        onClick={() => setServiceForm({ ...serviceForm, service_type: t })}
                        data-testid={`service-form-type-${t}`}
                        className={`flex-1 text-xs font-mono-plex tracking-widest border-r border-black last:border-r-0 transition-colors ${
                          serviceForm.service_type === t
                            ? `${TYPE_META[t].bg} ${TYPE_META[t].text}`
                            : "bg-white hover:bg-zinc-100"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <Input
                value={serviceForm.notes}
                onChange={(e) => setServiceForm({ ...serviceForm, notes: e.target.value })}
                data-testid="service-form-notes"
                placeholder="Notes (optional)"
                className="rounded-none border-black font-mono-plex text-xs h-8 mt-2"
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={saveService}
                  data-testid="service-form-save"
                  className="px-3 py-1.5 bg-black text-white text-[10px] font-mono-plex tracking-widest hover:bg-zinc-800 active:scale-[0.98]"
                >
                  {editingService ? "UPDATE" : "ADD"}
                </button>
                {editingService && (
                  <button
                    onClick={() => {
                      setEditingService(null);
                      setServiceForm({ service_date: "2026-04-01", service_type: "A", notes: "" });
                    }}
                    className="px-3 py-1.5 border border-black text-[10px] font-mono-plex tracking-widest hover:bg-zinc-100"
                  >
                    CANCEL EDIT
                  </button>
                )}
              </div>
            </div>

            <div className="border border-zinc-300 max-h-64 overflow-auto">
              <div className="grid grid-cols-[100px_60px_1fr_70px] font-heading font-bold text-[9px] tracking-widest border-b border-zinc-300 bg-zinc-50 sticky top-0">
                <div className="px-3 py-2">DATE</div>
                <div className="px-3 py-2 border-l border-zinc-200">TYPE</div>
                <div className="px-3 py-2 border-l border-zinc-200">NOTES</div>
                <div className="px-3 py-2 border-l border-zinc-200 text-right">ACT</div>
              </div>
              {(servicesByVehicle[servicesFor?.id] || []).length === 0 && (
                <div className="p-4 font-mono-plex text-[10px] text-zinc-500 tracking-widest">
                  NO SERVICE DAYS YET
                </div>
              )}
              {(servicesByVehicle[servicesFor?.id] || []).map((s) => (
                <div
                  key={s.id}
                  className="grid grid-cols-[100px_60px_1fr_70px] border-b border-zinc-200 last:border-b-0 hover:bg-zinc-50"
                  data-testid={`service-row-${s.id}`}
                >
                  <div className="px-3 py-2 font-mono-plex text-xs">{s.service_date}</div>
                  <div className="px-3 py-2 border-l border-zinc-200">
                    <span
                      className={`px-1.5 py-0.5 text-[10px] font-mono-plex font-bold ${TYPE_META[s.service_type].bg} ${TYPE_META[s.service_type].text}`}
                    >
                      {s.service_type}
                    </span>
                  </div>
                  <div className="px-3 py-2 border-l border-zinc-200 font-mono-plex text-[10px] text-zinc-700 truncate">
                    {s.notes || <span className="text-zinc-400">—</span>}
                  </div>
                  <div className="px-3 py-2 border-l border-zinc-200 flex items-center justify-end gap-1">
                    <button
                      onClick={() => startEditService(s)}
                      data-testid={`edit-service-${s.id}`}
                      className="p-1 border border-black hover:bg-black hover:text-white transition-colors"
                    >
                      <PencilSimple size={10} weight="bold" />
                    </button>
                    <button
                      onClick={() => delService(s)}
                      data-testid={`delete-service-${s.id}`}
                      className="p-1 border border-black hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                    >
                      <Trash size={10} weight="bold" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setServicesFor(null)}
              className="px-4 py-2 border border-black text-xs font-mono-plex tracking-widest hover:bg-zinc-100"
            >
              CLOSE
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Fleet Numbers dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[6px_6px_0_0_#0A0A0A] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tight">
              IMPORT FLEET NUMBERS
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-[11px] font-mono-plex text-zinc-600 leading-relaxed">
              Paste rows from your Excel vehicle tab. One line per vehicle:
              <span className="ml-1 bg-zinc-100 border border-zinc-300 px-1">route code</span>
              <span className="mx-1">then</span>
              <span className="bg-zinc-100 border border-zinc-300 px-1">fleet number</span>.
              Tab / comma / 2+ spaces all work as separators.
            </div>
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={"R1\tAB12 CDE\nR2\tCD34 EFG\nC1\tEF56 GHI"}
              data-testid="import-textarea"
              className="w-full h-48 border-2 border-black rounded-none p-3 font-mono-plex text-xs focus:outline-none focus:ring-2 focus:ring-black"
            />
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-zinc-300 p-3 bg-zinc-50">
                <div className="text-[10px] font-mono-plex tracking-widest text-zinc-600 mb-2">
                  PARSED PREVIEW ({importRows.length} ROWS)
                </div>
                <div className="max-h-32 overflow-auto font-mono-plex text-[10px] space-y-1">
                  {importRows.length === 0 && (
                    <div className="text-zinc-400">nothing parsed yet</div>
                  )}
                  {importRows.slice(0, 12).map((r, i) => {
                    const match = routes.find(
                      (rt) => rt.code.toUpperCase() === r.route_code.toUpperCase()
                    );
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-2 ${match ? "" : "text-rose-700"}`}
                      >
                        <span className="font-semibold w-16">{r.route_code}</span>
                        <span className="flex-1">{r.fleet_number}</span>
                        <span className="text-[9px] tracking-widest">
                          {match ? "✓ MATCH" : "✗ NO ROUTE"}
                        </span>
                      </div>
                    );
                  })}
                  {importRows.length > 12 && (
                    <div className="text-zinc-500">…and {importRows.length - 12} more</div>
                  )}
                </div>
              </div>
              <div className="border border-zinc-300 p-3 bg-zinc-50">
                <div className="text-[10px] font-mono-plex tracking-widest text-zinc-600 mb-2">
                  WHAT HAPPENS
                </div>
                <ul className="font-mono-plex text-[10px] space-y-1 text-zinc-700">
                  <li>• Existing vehicle on that route → fleet number replaced</li>
                  <li>• No vehicle on that route yet → new vehicle created</li>
                  <li>• Multiple rows for same route → additional vehicles added</li>
                  <li>• Unmatched route codes → skipped (shown above in red)</li>
                  <li className="text-emerald-700">• Services already logged are preserved</li>
                </ul>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setImportDialogOpen(false)}
              className="px-4 py-2 border border-black text-xs font-mono-plex tracking-widest hover:bg-zinc-100"
            >
              CANCEL
            </button>
            <button
              onClick={doImport}
              disabled={importing || importRows.length === 0}
              data-testid="import-fleet-confirm"
              className="px-4 py-2 bg-black text-white text-xs font-mono-plex tracking-widest hover:bg-zinc-800 active:scale-[0.98] disabled:opacity-50"
            >
              {importing ? "IMPORTING…" : `IMPORT ${importRows.length} ROW${importRows.length === 1 ? "" : "S"}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, icon, accent, testid }) {
  return (
    <div className="border-2 border-black bg-white p-4 flex items-center justify-between" data-testid={testid}>
      <div>
        <div className="text-[10px] font-mono-plex tracking-widest text-zinc-500">{label}</div>
        <div
          className={`font-heading font-black text-3xl tracking-tight leading-none mt-2 ${
            accent === "rose" ? "text-rose-600" : ""
          }`}
        >
          {value}
        </div>
      </div>
      {icon && (
        <div className={`p-3 ${accent === "rose" ? "bg-rose-600 text-white" : "bg-black text-white"}`}>
          {icon}
        </div>
      )}
    </div>
  );
}

function RoutePicker({ routes, value, onChange }) {
  const [open, setOpen] = useState(false);
  const current = routes.find((r) => r.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="vehicle-form-route"
          className="w-full flex items-center justify-between border border-black px-3 py-2 text-xs font-mono-plex hover:bg-zinc-100 transition-colors"
        >
          <span className={current ? "" : "text-zinc-500"}>
            {current ? `${current.code} · ${current.group}` : "SPARE (no route)"}
          </span>
          <CaretDown size={12} weight="bold" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] p-0 border-2 border-black rounded-none shadow-[4px_4px_0_0_#0A0A0A]"
      >
        <Command>
          <CommandInput placeholder="Search route…" className="font-mono-plex text-xs" />
          <CommandList className="max-h-64">
            <CommandEmpty>No route.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__spare__"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className="font-mono-plex text-xs"
              >
                <span className="flex-1 text-zinc-600">SPARE (no route)</span>
              </CommandItem>
              {routes.map((r) => (
                <CommandItem
                  key={r.id}
                  value={`${r.code} ${r.group}`}
                  onSelect={() => {
                    onChange(r.id);
                    setOpen(false);
                  }}
                  className="font-mono-plex text-xs"
                >
                  <span className="flex-1 font-semibold">{r.code}</span>
                  <span className="text-[9px] uppercase tracking-widest text-zinc-500">
                    {r.group}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ServiceHeatmap({ monthDate, onMonthChange, services, selectedDate, onSelectDate }) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const daysGrid = useMemo(() => {
    const first = new Date(year, month, 1);
    const firstDow = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const byDate = useMemo(() => {
    const m = {};
    for (const s of services) {
      const key = s.service_date;
      if (!m[key]) m[key] = { A: 0, AB: 0, C: 0, M: 0, total: 0 };
      m[key][s.service_type] = (m[key][s.service_type] || 0) + 1;
      m[key].total += 1;
    }
    return m;
  }, [services]);

  const maxTotal = Math.max(1, ...Object.values(byDate).map((v) => v.total));
  const bg = (t) => {
    if (!t) return "bg-white";
    const r = t / maxTotal;
    if (r > 0.75) return "bg-rose-500 text-white";
    if (r > 0.5) return "bg-amber-500 text-amber-950";
    if (r > 0.25) return "bg-sky-300";
    return "bg-sky-100";
  };

  return (
    <div className="border-2 border-black bg-white" data-testid="fleet-heatmap">
      <div className="px-4 py-3 border-b-2 border-black flex items-center justify-between">
        <div className="font-heading font-black text-lg tracking-tight leading-none">
          {MONTH_SHORT[month]} {year} · FLEET SERVICES
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 text-[10px] font-mono-plex tracking-widest text-zinc-600">
            {SERVICE_TYPES.map((t) => (
              <div key={t} className="flex items-center gap-1">
                <span className={`px-1 ${TYPE_META[t].bg} ${TYPE_META[t].text} font-bold`}>{t}</span>
              </div>
            ))}
          </div>
          <div className="flex border border-black">
            <button
              onClick={() => onMonthChange(new Date(year, month - 1, 1))}
              data-testid="fleet-heatmap-prev"
              className="px-2 h-8 border-r border-black hover:bg-zinc-100 active:scale-[0.98]"
            >
              <CaretLeft size={12} weight="bold" />
            </button>
            <button
              onClick={() => onMonthChange(new Date(2026, 3, 1))}
              className="px-3 h-8 border-r border-black hover:bg-zinc-100 text-[10px] font-mono-plex tracking-widest font-semibold"
            >
              APR 26
            </button>
            <button
              onClick={() => onMonthChange(new Date(year, month + 1, 1))}
              data-testid="fleet-heatmap-next"
              className="px-2 h-8 hover:bg-zinc-100 active:scale-[0.98]"
            >
              <CaretRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-zinc-300 bg-zinc-50">
        {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
          <div
            key={d}
            className="px-2 py-2 text-[10px] font-mono-plex tracking-widest text-zinc-500 border-r border-zinc-200 last:border-r-0 text-center"
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {daysGrid.map((d, i) => {
          if (!d) return <div key={`e-${i}`} className="border-r border-b border-zinc-200 h-20 bg-zinc-50" />;
          const iso = toISO(d);
          const stats = byDate[iso] || { A: 0, AB: 0, C: 0, M: 0, total: 0 };
          const isSelected = selectedDate === iso;
          return (
            <button
              key={iso}
              onClick={() => onSelectDate(iso === selectedDate ? null : iso)}
              data-testid={`fleet-heatmap-cell-${iso}`}
              className={`border-r border-b border-zinc-200 h-20 p-1.5 flex flex-col text-left transition-all ${bg(
                stats.total
              )} hover:outline hover:outline-2 hover:outline-black ${
                isSelected ? "outline outline-2 outline-black z-10" : ""
              }`}
            >
              <div className="flex items-center justify-between w-full">
                <span className="font-heading font-bold text-base leading-none">{d.getDate()}</span>
                {stats.total > 0 && (
                  <span className="font-mono-plex text-[9px] tracking-widest font-bold">{stats.total}</span>
                )}
              </div>
              {stats.total > 0 && (
                <div className="mt-auto flex items-center gap-0.5 flex-wrap">
                  {SERVICE_TYPES.map((t) =>
                    stats[t] > 0 ? (
                      <span
                        key={t}
                        className={`text-[8px] font-mono-plex tracking-widest px-1 py-0.5 leading-none font-bold ${TYPE_META[t].bg} ${TYPE_META[t].text}`}
                      >
                        {t}
                        {stats[t] > 1 ? stats[t] : ""}
                      </span>
                    ) : null
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
